import * as Api from '@ViewModels';
import { ESelectionState } from '@ViewModels';
import { css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import { parseAsArrayOf, parseAsInteger, parseAsString, useQueryStates } from 'nuqs';
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import {
	FullScreenModalViewModelKey,
	IFullscreenModalComponentProps,
	IQuickAddEntityComponentProps,
	IUserSessionComponentProps,
	QuickAddEntityViewModelKey,
	UserSessionViewModelKey,
} from '../../../../models/AppState';
import { useInput } from '../../../../models/hooks/inputHooks';
import { useCompaniesQuery, useCompanyFilterV2Query } from '../../../../queries/Company/useCompaniesQuery';
import { TextSearchIcon } from '../../../components/svgs/icons/TextSearchIcon';
import { Button } from '../../../components-v2/Button';
import {
	AppDrawer,
	AppDrawerContent,
	AppDrawerSeparator,
	AppDrawerToolbar,
	AppDrawerToolbarButton,
	AppDrawerToolbarButtonAiAssistant,
	StickyAppDrawerContent,
} from '../../../components-v2/Drawer/AppDrawer';
import { Column, Row } from '../../../components-v2/PageLayout';
import { ROUTES } from '../../../constants/routes';
import { bs } from '../../../styles/styles';
import { DefaultUniversalAssistantChat } from '../../templates/AssistantChat';
import { ContactsTableLoader } from '../shared/ContactsTableLoader';
import { CompaniesActionsBar } from './CompaniesActionsBar';
import {
	CompaniesSearchInputContext,
	CompanySearchContext,
	DefaultCompaniesSort,
	DefaultCompanySearchProperty,
} from './CompaniesContext';
import { CompaniesGlobalSearch } from './CompaniesGlobalSearch';
import { CompaniesClearSearchButton } from './CompaniesClearSearchButton';
import { CompaniesSearchDescription } from './CompaniesSearchDescription';
import { CompaniesTable } from './CompaniesTable';
import { CompanyTagFiltersDrawer } from './CompanyTagFiltersDrawer';
import { buildCompanyTagFilter, hasCompanyTagFilters } from './companyTagFilter';
import { styleSheet } from './styles';

// Tags are user-defined free text and may contain commas (nuqs' default array delimiter), which
// would corrupt the list on a URL round-trip. Use the ASCII Unit Separator instead — it can't appear
// in a typed tag name.
const TAG_ARRAY_DELIMITER = '\u001f';

export function useCompaniesPageUrlState() {
	return useQueryStates({
		companySearchProperty: parseAsInteger.withDefault(DefaultCompanySearchProperty),
		companySearchValue: parseAsString.withDefault(''),
		companyOrTags: parseAsArrayOf(parseAsString, TAG_ARRAY_DELIMITER).withDefault([]),
		companyAndTags: parseAsArrayOf(parseAsString, TAG_ARRAY_DELIMITER).withDefault([]),
		companyNotTags: parseAsArrayOf(parseAsString, TAG_ARRAY_DELIMITER).withDefault([]),
	});
}

export const CompaniesContainer: React.FC<
	IQuickAddEntityComponentProps & IFullscreenModalComponentProps & IUserSessionComponentProps
> = inject(
	QuickAddEntityViewModelKey,
	FullScreenModalViewModelKey,
	UserSessionViewModelKey
)(
	observer(props => {
		const { quickAddEntity } = props;
		const history = useHistory();
		const [{ companySearchProperty, companySearchValue, companyOrTags, companyAndTags, companyNotTags }] =
			useCompaniesPageUrlState();
		const querySort = DefaultCompaniesSort; // followup use nuqs for sort too

		const [selectionState, setSelectionState] = React.useState<ESelectionState>(ESelectionState.None);
		const [selectedCompanyIds, setSelectedCompanyIds] = React.useState<string[]>([]);
		const [excludedCompanyIds, setExcludedCompanyIds] = React.useState<string[]>([]);

		const companySearch = React.useMemo(
			() => ({ property: companySearchProperty, value: companySearchValue }) as Api.Search,
			[companySearchProperty, companySearchValue]
		);

		// Advanced tag search (v2 recursive criteria). When tag filters are active, an active name
		// search composes in the same request as a case-insensitive NamePrefix (starts-with) match.
		// A plain name search with no tag filters still uses the v1 endpoint (default behavior).
		const tagFilterLists = React.useMemo(
			() => ({ orTags: companyOrTags, andTags: companyAndTags, notTags: companyNotTags }),
			[companyOrTags, companyAndTags, companyNotTags]
		);
		const hasTagFilters = hasCompanyTagFilters(tagFilterLists);
		// The Tag tab does its own single-tag search via v1, so only treat Name/All values as a name prefix.
		const namePrefix = companySearchProperty !== Api.CompanySearchRequestProperty.Tag ? companySearchValue : undefined;
		const companyTagFilter = React.useMemo(
			() => buildCompanyTagFilter(tagFilterLists, namePrefix),
			[tagFilterLists, namePrefix]
		);

		const [sort, setSort] = React.useState<Api.ISortDescriptor>(querySort);
		const companiesQuery = useCompaniesQuery({
			searchRequest: { searches: [companySearch] },
			sortDescriptor: sort,
			enabled: !hasTagFilters,
		});
		const companiesV2Query = useCompanyFilterV2Query({
			filter: companyTagFilter,
			sortDescriptor: sort,
			enabled: hasTagFilters,
		});
		const activeQuery = hasTagFilters ? companiesV2Query : companiesQuery;

		const companies = React.useMemo(
			() => (activeQuery.data?.pages?.flatMap(page => page.values) ?? []) as Api.ICompany[],
			[activeQuery.data]
		);

		// Reset selection whenever the active search/filter changes so stale selections don't carry over.
		const tagFilterKey = JSON.stringify([companyOrTags, companyAndTags, companyNotTags]);
		React.useEffect(() => {
			setSelectionState(ESelectionState.None);
			setSelectedCompanyIds([]);
			setExcludedCompanyIds([]);
		}, [companySearchProperty, companySearchValue, tagFilterKey]);

		const filterValue = React.useMemo(
			() => ({
				companies,
				sort,
				setSort,
				selectionState,
				setSelectionState,
				selectedCompanyIds,
				setSelectedCompanyIds,
				excludedCompanyIds,
				setExcludedCompanyIds,
				companiesQuery: activeQuery,
			}),
			[
				sort,
				setSort,
				companies,
				selectionState,
				setSelectionState,
				selectedCompanyIds,
				setSelectedCompanyIds,
				excludedCompanyIds,
				setExcludedCompanyIds,
				activeQuery,
			]
		);

		// Search Input Context

		const [search, setSearch, onSearchChange] = useInput(companySearch.value ?? '');
		// eslint-disable-next-line react-hooks/exhaustive-deps
		const searchValue = React.useMemo(() => ({ onSearchChange, search, setSearch }), [search]);

		React.useEffect(() => {
			setSearch(companySearchValue);
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [companySearchValue]);

		// "New Company" modal
		const onShowQuickAddContactModal = React.useCallback(() => {
			quickAddEntity.show({
				entity: {},
				onComplete: (error: Api.IOperationResultNoValue, companyModel?: Api.ICompany) => {
					if (!error && !!companyModel) {
						quickAddEntity.reset();
						history.push({
							pathname: `${ROUTES.contacts.companies}/${companyModel.id}`,
						});
					}
				},
				type: 'company',
			});
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, []);

		return (
			<>
				<CompaniesSearchInputContext.Provider value={searchValue}>
					<CompanySearchContext.Provider value={filterValue}>
						<Column styles={[bs.gap4, styleSheet.container]}>
							<Row styles={[styleSheet.searchRow]}>
								<Row styles={[styleSheet.searchControls]}>
									<CompaniesGlobalSearch />
									<CompaniesClearSearchButton />
								</Row>
								<Button onClick={onShowQuickAddContactModal}>New Company</Button>
							</Row>
							<div className={css(styleSheet.searchDescription)}>
								<CompaniesSearchDescription />
							</div>
							<CompaniesActionsBar />
							{activeQuery.isPending && !companies.length ? <ContactsTableLoader /> : <CompaniesTable />}
						</Column>
					</CompanySearchContext.Provider>
				</CompaniesSearchInputContext.Provider>
				<AppDrawer>
					<AppDrawerContent value='filter'>
						<CompanyTagFiltersDrawer />
					</AppDrawerContent>
					<StickyAppDrawerContent value='ai-assistant'>
						<DefaultUniversalAssistantChat />
					</StickyAppDrawerContent>
					<AppDrawerToolbar>
						<AppDrawerToolbarButtonAiAssistant value='ai-assistant' />
						<AppDrawerSeparator />
						<AppDrawerToolbarButton value='filter' tooltip='Filter companies'>
							<TextSearchIcon />
						</AppDrawerToolbarButton>
					</AppDrawerToolbar>
				</AppDrawer>
			</>
		);
	})
);
